import { CSSProperties, FC } from 'react';
import { Sidebar, Menu, MenuItem, SubMenu, menuClasses } from 'react-pro-sidebar';
import { Link, useLocation } from 'react-router-dom';
import { FiBox, FiArrowLeft, FiHome } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { Typography } from '@mui/material';

import { IInsightsPage } from '../../../logic/api';
import usePaths from '../../../utility/paths';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import { type Permission } from '@/components/users/rolesAndPermissions/types';
import { ReleaseFlag, useReleaseManagement } from '@/utility/feature-management';


interface IInsightsSidebarProps {
  pages?: IInsightsPage[];
  style?: CSSProperties;
}

const INACTIVE_ICON_COLOR = '#bdbdbd';

export const InsightsSidebar: FC<IInsightsSidebarProps> = ({ pages, style }) => {
  const paths = usePaths();
  const { pathname: currentPath } = useLocation();
  const { t } = useTranslation();
  const { hasPermission } = useUserPermissions();
  const isRbacInsightsCategoriesReleased = useReleaseManagement(ReleaseFlag.releaseRbacPermissionsInsightsCategories);

  const [homePage, ...otherPages] = pages || [];
  const pagesGroupedBySubtitle = otherPages.reduce((acc, curr) => {
    if (!acc[curr.subTitle]) {
      acc[curr.subTitle] = [];
    }

    acc[curr.subTitle].push(curr);

    return acc
  }, {} as Record<string, IInsightsPage[]>);

  const canViewPage = (page: IInsightsPage): boolean =>
    !isRbacInsightsCategoriesReleased ||
    !page.permission ||
    hasPermission(page.permission as Permission);

  const isHomePageAccessible = homePage ? canViewPage(homePage) : false;

  return (
    <Sidebar
      style={style}
      rootStyles={{
        backgroundColor: "white",
        [`.${menuClasses.button}`]: {
          padding: 4,
          [`&.${menuClasses.active}`]: {
            borderLeft: "2px solid #EA3913",
          },
        },
        [`.${menuClasses.subMenuRoot}`]: {
          backgroundColor: "#f8fafd",
          [`.${menuClasses.label}`]: {
            fontWeight: "bold",
          },
        },
        [`.${menuClasses.subMenuContent}`]: {
          [`.${menuClasses.label}`]: {
            fontWeight: "normal",
            [`&.${menuClasses.active}`]: {
              fontWeight: "bold"
            },
          },
          [`.${menuClasses.button}`]: {
            borderBottom: '1px solid #eee'
          }
        },
      }}
    >
      <Typography
        variant="subtitle2"
        fontWeight="bold"
        padding={2}
      >
        {t("insights.title")}
      </Typography>
      <Menu>
        {homePage && (
          <MenuItem
            active={isHomePageAccessible && currentPath === paths.insights(homePage.id)}
            disabled={!isHomePageAccessible}
            component={isHomePageAccessible ? <Link to={paths.insights(homePage.id)} /> : undefined}
            icon={<FiHome color={isHomePageAccessible ? '#3498db' : INACTIVE_ICON_COLOR} />}
            title={isHomePageAccessible ? undefined : t("insights.noCategoryPermission")}
            style={{
              fontWeight: "bold",
              backgroundColor: "#f8fafd",
              borderTop: "1px solid #eee"
            }}
          >
            {homePage.pageTitle}
          </MenuItem>
        )}
        {
          Object.entries(pagesGroupedBySubtitle).map(([subMenuTitle, subMenuItems]) => {
            const subMenuIsActive = subMenuItems.some(i => canViewPage(i) && currentPath === paths.insights(i.id))
            const firstViewableItem = subMenuItems.find(canViewPage)
            const isGroupAccessible = firstViewableItem !== undefined

            return (
              <SubMenu
                key={subMenuTitle}
                label={subMenuTitle}
                active={subMenuIsActive}
                disabled={!isGroupAccessible}
                icon={<FiBox color={isGroupAccessible ? '#2ecc71' : INACTIVE_ICON_COLOR} />}
                component={firstViewableItem ? <Link to={paths.insights(firstViewableItem.id)} /> : undefined}
                open={subMenuIsActive}
                title={isGroupAccessible ? undefined : t("insights.noCategoryPermission")}
              >
                {subMenuItems.map((item) => {
                  const isItemAccessible = canViewPage(item)

                  return (
                    <MenuItem
                      key={item.id}
                      active={isItemAccessible && currentPath === paths.insights(item.id)}
                      disabled={!isItemAccessible}
                      component={isItemAccessible ? <Link to={paths.insights(item.id)} /> : undefined}
                      icon={<FiArrowLeft color={isItemAccessible ? '#EA3913' : INACTIVE_ICON_COLOR} />}
                      title={isItemAccessible ? undefined : t("insights.noCategoryPermission")}
                    >
                      {item.pageTitle}
                    </MenuItem>
                  )
                })}
              </SubMenu>
            )
          })
        }
      </Menu>
    </Sidebar>
  )
};
